<template>
  <div class="relative w-full h-[500px] md:h-[600px] overflow-hidden bg-gray-900">
    <!-- Slider Background -->
    <div v-if="banners && banners.length > 0" class="absolute inset-0 w-full h-full">
      <transition-group name="fade" tag="div">
        <div
          v-for="(banner, index) in banners"
          :key="banner.id"
          v-show="currentSlide === index"
          class="absolute inset-0 w-full h-full"
        >
          <img
            :src="banner.photo"
            class="w-full h-full object-cover opacity-60 transition-transform duration-[10000ms] ease-linear transform scale-100 hover:scale-110"
            alt="Banner"
          />
          <div class="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent"></div>
        </div>
      </transition-group>
    </div>
    
    <!-- Fallback Background if no banners -->
    <div v-else class="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-900 to-indigo-900">
      <div class="absolute inset-0 opacity-20 bg-[radial-gradient(circle,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:20px_20px]"></div>
    </div>

    <!-- Content Overlay -->
    <div class="absolute inset-0 flex flex-col items-center justify-center px-4 z-20">
      <!-- Language Selector -->
      <div class="absolute top-6 right-6 z-30">
        <div class="relative inline-block text-left">
            <select
              v-model="selectedLanguage"
              class="appearance-none bg-white/10 backdrop-blur-md border border-white/20 text-white py-2 pl-4 pr-10 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer text-sm font-medium hover:bg-white/20 transition-colors"
            >
              <option class="text-gray-900" v-for="(lang, i) in $i18n.availableLocales" :key="`Lang${i}`" :value="lang">
                {{ lang.toUpperCase() }}
              </option>
            </select>
             <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
        </div>
      </div>

      <div class="text-center max-w-4xl mx-auto space-y-6 animate-fade-in-up">
        <h1 class="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight drop-shadow-lg">
          {{ $t('landingpageHeader') }}
        </h1>
        <p class="text-lg md:text-2xl text-gray-200 font-light max-w-2xl mx-auto drop-shadow-md">
           {{ $t("landingpageSubHeader") }}
        </p>

        <!-- Search Bar -->
        <div class="w-full max-w-2xl mx-auto mt-10 relative group">
          <div class="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
          <div class="relative flex items-center bg-white p-2 rounded-2xl shadow-2xl">
            <div class="flex-1 flex items-center px-4">
              <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
              <input
                type="text"
                v-model="search"
                class="w-full p-3 text-gray-700 bg-transparent border-none focus:ring-0 placeholder-gray-400 text-lg"
                :placeholder="$t('What are you looking for today?')"
                @keyup.enter="getSearchResult"
              />
            </div>
            <button
               @click="getSearchResult"
               class="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all duration-300 shadow-lg shadow-blue-500/30 transform active:scale-95"
               :style="{ 'background-color': settings ? settings.colors.primaryColor : '' }"
            >
              {{ $t('Search') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Slider Indicators -->
    <div v-if="banners && banners.length > 1" class="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
      <button
        v-for="(banner, index) in banners"
        :key="index"
        @click="setCurrentSlide(index)"
        class="w-3 h-3 rounded-full transition-all duration-300"
        :class="currentSlide === index ? 'bg-white w-8' : 'bg-white/40 hover:bg-white/70'"
      ></button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import router from "@/router";

export default {
  name: "HomeBanner",
  data() {
    return {
      search: null,
      base_url: this.$store.state.baseUrl,
      selectedLanguage: this.$i18n.locale,
      settings: null,
      banners: [],
      currentSlide: 0,
      slideInterval: null,
    };
  },
  watch: {
    selectedLanguage(newLang) {
      localStorage.setItem("selectedLanguage", newLang);
      this.$i18n.locale = newLang;
      // Refresh banners if they are localized? Usually banners might be language specific
    },
  },
  mounted() {
    this.fetchSettings();
    this.fetchBanners();
  },
  beforeUnmount() {
    this.stopSlideTimer();
  },
  methods: {
    fetchSettings() {
      axios
        .get(this.base_url + "api/app/settings")
        .then((response) => {
          this.settings = response.data;
        })
        .catch((error) => console.log(error));
    },
    fetchBanners() {
      axios
        .get(this.base_url + "api/banners")
        .then((response) => {
          this.banners = response.data.data || response.data; // Handle pagination or direct array
          if (this.banners.length > 1) {
            this.startSlideTimer();
          }
        })
        .catch((error) => console.log("Error fetching banners:", error));
    },
    getSearchResult() {
      if (this.search) {
        router.push({ name: "DefaultSearch", params: { slug: this.search } });
      }
    },
    setCurrentSlide(index) {
      this.currentSlide = index;
      this.stopSlideTimer();
      this.startSlideTimer();
    },
    startSlideTimer() {
        this.slideInterval = setInterval(() => {
            this.currentSlide = (this.currentSlide + 1) % this.banners.length;
        }, 5000);
    },
    stopSlideTimer() {
        if (this.slideInterval) {
            clearInterval(this.slideInterval);
        }
    }
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translate3d(0, 40px, 0);
    }
    to {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
}
.animate-fade-in-up {
    animation: fadeInUp 0.8s ease-out forwards;
}
</style>
